import { Contact, TableColumnType, ListingEntry } from 'components';
import { useHistory } from 'react-router-dom';
import { AuthContext, ContactDrawerType, FeedbackModalContext } from 'contexts';
import { useBreakpoint } from 'hooks';
import React, { useContext, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import {
  AppointmentStatus,
  AppointmentType,
  TourItemType,
} from '@bb/domain/dist/types';
import type { AppointmentActionModalContextType } from 'contexts/modal/types';
import { getInternalShowingDetailsUrl, getLocalTimeZone } from 'utils';
import { StatusEntry } from '../../../components';
import { ES_DELAY } from '../constants';
import { useShowingsCalendarContext } from '../ShowingsCalendarContext';
import { BuyerTableData, CommonInnerTableProps, TableRenderFn } from '../types';
import { buyerNeedsAction } from '../utils';
import {
  ActionsEntry,
  Covid19Entry,
  FeedbackEntry,
  StyledInnerTable,
  StyledMobileColumn,
} from './components';
import DotOrCheckbox from './components/DotOrCheckbox';
import TourRouteStopItem from './components/TourRouteStopItem';
import { buildTableComponents } from './tableUtils';
import { useWriteCalendarHover } from '../ShowingsCalendarHoverContext';

const BuyerTable: React.FC<CommonInnerTableProps<BuyerTableData>> = ({
  tableKey,
  showings,
  setSelectedCovidAppt,
  isTourTable,
}) => {
  const { auth } = useContext(AuthContext);
  const { refetchPage } = useShowingsCalendarContext();
  const {
    openModal: openFeedbackModal,
    closeModal,
    visible,
  } = useContext(FeedbackModalContext);
  const { setHoveredAppointment } = useWriteCalendarHover();

  const history = useHistory();
  const { md } = useBreakpoint();

  useEffect(() => () => {
      if (visible) closeModal();
    }, [visible]);

  const { hasOneTourShowing, hasOneTourItem } = useMemo(() => ({
      hasOneTourShowing:
        isTourTable &&
        showings.filter(showing => showing.type !== 'ROUTE_STOP').length <= 1,
      hasOneTourItem: isTourTable && showings.length <= 1,
    }), [showings, isTourTable]);

  const renderStatusEntry: TableRenderFn<BuyerTableData[number]> = (
    _,
    { displayStatus, type },
  ) => type !== 'ROUTE_STOP' ? (
      <StatusEntry isBuyer displayStatus={displayStatus} type={type} />
    ) : (
      <></>
    );

  const renderListingEntry: TableRenderFn<BuyerTableData[number]> = (
    _,
    {
      id,
      covid19RequestedAck,
      covid19Signed,
      status,
      time,
      timeEnd,
      listing,
      listingContactTimeChange,
      listingId,
      type,
      locationName,
      address,
      originalTime,
      originalTimeEnd,
      tourName,
      passed,
    },
    i,
    opts,
  ) => {
    const needsAction = buyerNeedsAction({
      status: status as AppointmentStatus,
      listingContactTimeChange,
      covid19RequestedAck,
      covid19Signed,
      timeEnd,
    });
    return (
      <>
        {isTourTable && md && i !== 0 && <TourItemConnectLine $position="up" />}
        {type !== 'ROUTE_STOP' ? (
          <ListingEntry
            listingEntryExtraContent={
              <DotOrCheckbox
                appointmentId={id}
                dotVisible={needsAction}
                status={status as AppointmentStatus}
                passed={passed}
              />
            }
            time={time}
            timeEnd={timeEnd}
            timezone={getLocalTimeZone(auth)}
            line1={listing.line1}
            line2={listing.line2}
            thumbnail={listing.thumbnail}
            listingId={listingId || listing.id}
            originalTime={originalTime}
            originalTimeEnd={originalTimeEnd}
            status={status as AppointmentStatus}
            tourName={tourName}
            needsAction={needsAction}
          />
        ) : (
          <TourRouteStopItem
            locationName={locationName}
            address={address}
            isMobile={opts?.isMobile}
          />
        )}
        {isTourTable && md && i !== showings.length - 1 && (
          <TourItemConnectLine $position="down" />
        )}
      </>
    );
  };

  const renderBuyers: TableRenderFn<BuyerTableData[number]> = (
    _,
    { buyers },
    i,
    opts,
  ) => {
    if (!buyers || buyers.length === 0) return <></>;

    if (buyers.length > 2) {
      return (
        <StyledBuyers
          isMobile={opts?.isMobile}
          onClick={e => e.stopPropagation()}
        >
          <Contact
            key={`${i}_buyer`}
            contact={buyers[0]}
            truncateLastName
            type={ContactDrawerType.Contact}
            tooltipPlacement="top"
            viewNameOnToolTip
            additionalContacts={buyers.slice(1)}
          />
        </StyledBuyers>
      );
    }

    return (
      <StyledBuyers
        isMobile={opts?.isMobile}
        onClick={e => e.stopPropagation()}
      >
        {buyers.map(({ id, firstName, lastName }) => (
          <Contact
            key={`${i}_buyer_${firstName}_${lastName}`}
            contact={{ id, firstName, lastName }}
            type={ContactDrawerType.Contact}
            viewNameOnToolTip
            tooltipPlacement="top"
            truncateLastName
          />
        ))}
      </StyledBuyers>
    );
  };

  const renderSecondaryActions: TableRenderFn<BuyerTableData[number]> = (
    _,
    {
      covid19Overridden,
      covid19RequestedAck,
      covid19Signed,
      orgHasCovidFlag,
      feedbackCollected,
      id,
      listingId,
      passed,
      signature,
      status,
      timeEnd,
      type,
    },
    i,
    opts,
  ) => {
    if (type === 'APPOINTMENT' || type === 'ROUTE_STOP') return <></>;
    return (
      <>
        {orgHasCovidFlag && (
          <Covid19Entry
            isBuyer
            covid19RequestedAck={covid19RequestedAck}
            covid19Signed={covid19Signed}
            covid19Overridden={covid19Overridden}
            signatureType={signature?.template ? 'template' : 'pdf'}
            status={status as AppointmentStatus}
            timeEnd={timeEnd}
            // @ts-ignore BuildJsonType does not go deep enough to support this
            onSignPrompt={() => setSelectedCovidAppt(showings[i])}
            onSignatureView={() =>
              // @ts-ignore BuildJsonType does not go deep enough to support this
              signature?.template ? setSelectedCovidAppt(showings[i]) : null
            }
            isMobile={opts?.isMobile}
          />
        )}
        <FeedbackEntry
          feedbackCollected={feedbackCollected}
          isBuyer
          status={status as AppointmentStatus}
          type={type as AppointmentType}
          passed={Boolean(passed)}
          onClick={mode =>
            openFeedbackModal({
              modalType: 'coop',
              modalMode: mode,
              listingId,
              appointmentId: id,
              onSubmit: () => refetchPage(ES_DELAY.DEFAULT),
            })
          }
          isMobile={opts?.isMobile}
        />
      </>
    );
  };

  const renderPrimaryActions: TableRenderFn<BuyerTableData[number]> = (
    _,
    appointment,
    i,
    opts,
  ) => {
    if (
      appointment.type === TourItemType.APPOINTMENT ||
      appointment.type === TourItemType.ROUTE_STOP
    )
      return <></>;

    return (
      <ActionsEntry
        isBuyer
        hasOneTourShowing={hasOneTourShowing}
        hasOneTourItem={hasOneTourItem}
        canViewDetails={false}
        appointment={
          appointment as unknown as AppointmentActionModalContextType['appointment']
        }
        isTourItem={Boolean(appointment.tourName)}
        isMobile={opts?.isMobile}
      />
    );
  };

  const renderMobile: TableRenderFn<BuyerTableData[number]> = (
    _,
    appointment,
    i,
  ) => (
    <StyledMobileColumn>
      {[
        renderStatusEntry(_, appointment, i),
        renderListingEntry(_, appointment, i, { isMobile: true }),
        renderBuyers(_, appointment, i, { isMobile: true }),
        renderPrimaryActions(_, appointment, i, { isMobile: true }),
        renderSecondaryActions(_, appointment, i, { isMobile: true }),
      ]}
    </StyledMobileColumn>
  );

  const columns: TableColumnType<BuyerTableData[number]>[] = md
    ? // Desktop View
      [
        {
          key: 'geo',
          dataIndex: '',
          width: 275,
          render: renderListingEntry,
        },
        {
          key: 'status',
          dataIndex: '',
          width: 250,
          render: renderStatusEntry,
        },
        {
          key: 'buyers',
          dataIndex: '',
          render: renderBuyers,
        },
        {
          key: 'secondaryActions',
          dataIndex: '',
          render: renderSecondaryActions,
        },
        {
          key: 'primaryActions',
          dataIndex: '',
          render: renderPrimaryActions,
        },
      ]
    : // Mobile View
      [
        {
          key: 'mobile',
          dataIndex: '',
          render: renderMobile,
        },
      ];

  return (
    <StyledInnerTable
      components={buildTableComponents({ isTourTable, md })}
      key={tableKey}
      columns={columns}
      rowKey={record => record.id}
      showHeader={false}
      dataSource={showings}
      pagination={false}
      data-cy="buyer-table"
      onRow={({ id, type }) => ({
          onClick: () => {
            if (['ROUTE_STOP', 'APPOINTMENT'].includes(type)) {
              return;
            }
            history.push(
              `${getInternalShowingDetailsUrl(id)}?redirectTo=calendar`,
            );
          },
          onMouseEnter: () => setHoveredAppointment(id),
          onMouseLeave: () => setHoveredAppointment(null),
        })}
    />
  );
};

export default BuyerTable;

const TourItemConnectLine = styled.div<{ $position: 'up' | 'down' }>`
  position: absolute;
  background-color: #d9d9d9;
  width: 2px;
  height: 50%;
  left: 47px;
  ${({ $position }) => ($position === 'up' ? 'top : -2px;' : 'top: 50%;')}
  z-index: 1;
`;

const StyledBuyers = styled.div<{ isMobile?: boolean }>`
  ${({ isMobile }) => isMobile && 'margin-top: 10px; margin-bottom: 10px;'}
`;
